import { memo, useRef, useEffect, useState } from 'react';
import { WorkflowBlock as WorkflowBlockType, PortSide } from '@/types/workflow';
import { cn } from '@/lib/utils';
import { IconPicker, getBlockIcon } from './IconPicker';
import { BLOCK_WIDTH_PX, BLOCK_HEIGHT_PX } from '@/constants/canvas';
import { hyphenateName, maxCharsForWidth } from '@/lib/nameHyphenate';
import { Layers, Paperclip, FileText } from 'lucide-react';
import { dispatchOnboardingEvent } from '@/components/onboarding/uiAdapter';
import { useEntityDetailsDialog } from '@/contexts/useEntityDetailsDialog';
import { useEntityDetailsIndex } from '@/hooks/useEntityDetails';
import { isSheetLinkBlock } from '@/lib/sheetLink';
import { SheetLinkBadge } from '@/components/atlas/SheetLinkBadge';

interface WorkflowBlockProps {
  block: WorkflowBlockType;
  isSelected: boolean;
  isConnecting: boolean;
  isGrouping: boolean;
  isInGroupSelection: boolean;
  pendingConnection: boolean;
  /** True while cross-sheet linking mode is active — block gets a subtle
   *  "you can pick me" hover indication. */
  isCrossSheetMode?: boolean;
  /** True when this specific block is the cross-sheet link source. */
  isCrossSheetSource?: boolean;
  onSelect: () => void;
  onDragStart: (e: React.MouseEvent) => void;
  onPortClick: (blockId: string, port: PortSide) => void;
  onUpdateIcon?: (blockId: string, iconKey: string) => void;
  onResize?: (blockId: string, width: number, height: number) => void;
  onConvertToLayer?: (blockId: string) => void;
  /** Double-click on the block body — opens the edit dialog in Index. */
  onEditRequest?: (blockId: string) => void;
  viewOnly?: boolean;
  tabIndex?: number;
  onFocus?: () => void;
}

const DRAG_EVENT_THRESHOLD_PX = 12;

export const WorkflowBlock = memo(function WorkflowBlock({
  block,
  isSelected,
  isConnecting,
  isGrouping,
  isInGroupSelection,
  pendingConnection,
  isCrossSheetMode = false,
  isCrossSheetSource = false,
  onSelect,
  onDragStart,
  onPortClick,
  onUpdateIcon,
  onResize,
  onConvertToLayer,
  onEditRequest,
  viewOnly,
  tabIndex = -1,
  onFocus,
}: WorkflowBlockProps) {
  const blockRef = useRef<HTMLDivElement>(null);
  const FallbackIcon = getBlockIcon(block.type, block.config.icon);
  // Indicator: hämta entity_details-index för current workflow så vi kan
  // visa paperclip / note-ikon på blocks som har details/attachments.
  // useQuery dedup:ar requests så kostnaden är 1 fetch per workflow,
  // inte per block.
  const { workflowId } = useEntityDetailsDialog();
  const { data: detailsIndex } = useEntityDetailsIndex(workflowId);
  const indicator = detailsIndex?.[block.id];
  // Track recent click time so a fast second click suppresses drag-start
  // and lets the dblclick handler take over (no phantom drag).
  const lastClickTimeRef = useRef(0);

  useEffect(() => {
    if (blockRef.current) {
      blockRef.current.dataset.blockId = block.id;
    }
  }, [block.id]);

  // Every block has the same footprint — no auto-sizing. This stops the
  // canvas from "warping" when a long description renders a tall card
  // next to a short one. Long descriptions are now surfaced via the
  // EntityDetailsDialog instead. Size is reported once on mount so the
  // minimap / group-frame math still sees the dimensions.
  useEffect(() => {
    const el = blockRef.current;
    if (!el) return;
    el.style.width = `${BLOCK_WIDTH_PX}px`;
    el.style.height = `${BLOCK_HEIGHT_PX}px`;
    onResize?.(block.id, BLOCK_WIDTH_PX, BLOCK_HEIGHT_PX);
  }, [block.id, onResize]);

  const downPosRef = useRef<{ x: number; y: number } | null>(null);
  const movedRef = useRef(false);
  // Mirror of movedRef for rendering: toggles a `data-drag-active` attribute
  // so CSS can apply a `scale(1.02)` + `cursor-grabbing` while the user is
  // dragging past the threshold. Purely visual, no behaviour change.
  const [isDragActive, setIsDragActive] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect();
    dispatchOnboardingEvent({ name: 'block:click', target: 'block', data: { blockId: block.id } });

    // Phantom-drag suppression: if this mousedown lands within the OS
    // double-click window (~300ms) of the previous one, treat it as part
    // of a dblclick gesture and skip the drag-start watcher entirely.
    const now = Date.now();
    const isDblClickFollowup = now - lastClickTimeRef.current < 300;
    lastClickTimeRef.current = now;

    downPosRef.current = { x: e.clientX, y: e.clientY };
    movedRef.current = false;

    const onMove = (ev: MouseEvent) => {
      if (!downPosRef.current) return;
      const dx = ev.clientX - downPosRef.current.x;
      const dy = ev.clientY - downPosRef.current.y;
      if (!movedRef.current && dx * dx + dy * dy > DRAG_EVENT_THRESHOLD_PX * DRAG_EVENT_THRESHOLD_PX) {
        movedRef.current = true;
        setIsDragActive(true);
        dispatchOnboardingEvent({ name: 'block:moved', target: 'block', data: { blockId: block.id } });
      }
    };
    const onUp = () => {
      downPosRef.current = null;
      setIsDragActive(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    // Skip drag-start in cross-sheet mode (the click should pick the block,
    // not start a drag) and on the second click of a dblclick.
    if (!viewOnly && !isConnecting && !isGrouping && !isCrossSheetMode && !isDblClickFollowup) {
      onDragStart(e);
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatchOnboardingEvent({ name: 'block:dblclick', target: 'block', data: { blockId: block.id } });
    if (!viewOnly && !isCrossSheetMode) {
      onEditRequest?.(block.id);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    onSelect();
    // Drag-start är AVSIKTLIGT inte trigad här. canvas-touch.ts
    // committar block-drag först när rörelse > TAP_MOVEMENT_THRESHOLD.
    // Tidigare anropade vi onDragStart direkt → isDraggingRef sattes
    // omedelbart → första touchmove-jittret cancellerade long-press-
    // timern → context-menu kunde aldrig öppnas via tap-and-hold.
  };

  const handlePortClick = (port: PortSide) => (e: React.MouseEvent) => {
    e.stopPropagation();
    // In cross-sheet mode the port hit-test is inert: route the click as a
    // whole-block pick instead so the chokepoint can decide source vs target.
    // Without this, a port click would feed the regular connector flow and
    // bypass cross-sheet routing entirely.
    if (isCrossSheetMode) {
      onSelect();
      return;
    }
    onPortClick(block.id, port);
  };

  const handleIconSelect = (iconKey: string) => {
    onUpdateIcon?.(block.id, iconKey);
  };

  const handleConvertToLayer = (e: React.MouseEvent) => {
    e.stopPropagation();
    onConvertToLayer?.(block.id);
  };

  // Sheet-link blocks get a dashed border to distinguish them from
  // regular blocks. The border-style is applied here (override of the
  // default solid border-2) so the SheetLinkBadge can sit cleanly on top.
  const isLinkBlock = isSheetLinkBlock(block);

  return (
    <div
      ref={blockRef}
      data-block-id={block.id}
      data-guide="block"
      className={cn(
        "absolute rounded-xl border-2 bg-card cursor-move select-none transition-shadow transition-colors duration-150 pointer-events-auto overflow-hidden",
        isSelected && "block-shadow-selected",
        !isSelected && "block-shadow hover:shadow-lg",
        viewOnly && "cursor-default",
        !viewOnly && isConnecting && "cursor-pointer",
        !viewOnly && isGrouping && "cursor-pointer",
        isInGroupSelection && "ring-2 ring-accent ring-offset-2",
        pendingConnection && "ring-2 ring-primary ring-offset-2",
        isCrossSheetMode && !isCrossSheetSource && "cursor-pointer hover:ring-2 hover:ring-sky-400 hover:ring-offset-2",
        isCrossSheetSource && "ring-4 ring-sky-500 ring-offset-2 animate-pulse shadow-lg shadow-sky-500/50",
        // Sheet-link blocks: dashed border + slight tint so they stand
        // apart visually as "external pointer" rather than first-class
        // workflow content.
        isLinkBlock && "border-dashed",
      )}
      data-sheet-link={isLinkBlock || undefined}
      data-drag-active={isDragActive || undefined}
      style={{
        left: block.x,
        top: block.y,
        // Drag-active visuellt feedback: 1.03 scale (uniform-zoom hanteras
        // av canvas-wrapper via useCanvasScale, inte per-block).
        transform: `translate(-50%, -50%) scale(${isDragActive ? 1.03 : 1})`,
        transition: 'transform 80ms ease-out',
        cursor: isDragActive ? 'grabbing' : undefined,
        borderColor: isSelected ? `hsl(var(--block-selected))` : block.color
      }}
      tabIndex={tabIndex}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onTouchStart={handleTouchStart}
      onFocus={onFocus}
    >
      {!viewOnly && (
        <>
          {(['top', 'top-right', 'right', 'bottom-right', 'bottom', 'bottom-left', 'left', 'top-left'] as PortSide[]).map((port) => {
            const isCardinal = ['top', 'right', 'bottom', 'left'].includes(port);
            const posClasses: Record<string, string> = {
              'top': 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2',
              'top-right': 'right-0 top-0 translate-x-1/3 -translate-y-1/3',
              'right': 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2',
              'bottom-right': 'right-0 bottom-0 translate-x-1/3 translate-y-1/3',
              'bottom': 'left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2',
              'bottom-left': 'left-0 bottom-0 -translate-x-1/3 translate-y-1/3',
              'left': 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2',
              'top-left': 'left-0 top-0 -translate-x-1/3 -translate-y-1/3',
            };
            return (
              <div
                key={port}
                role="button"
                aria-label={`Connect ${port} port`}
                tabIndex={0}
                className={cn(
                  "absolute rounded-full border-2 bg-card cursor-crosshair transition-all hover:bg-primary hover:border-primary hover:scale-125 z-10",
                  posClasses[port],
                  isCardinal ? "w-3.5 h-3.5" : "w-3 h-3",
                  // #56: under connect-mode pulserar target-portarna så
                  // user ser exakt vilka portar som snappar — annars
                  // måste user gissa sig fram blockvis.
                  isConnecting && !pendingConnection && "ring-2 ring-primary/40 animate-pulse",
                )}
                style={{ borderColor: block.color }}
                data-port={port}
                data-guide={`port-${port}`}
                data-testid={`port-${port}`}
                title={port}
                onClick={handlePortClick(port)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPortClick(block.id, port); } }}
                onMouseDown={(e) => e.stopPropagation()}
              />
            );
          })}
        </>
      )}

      {/* Sheet-link badge — top-left overlay icon + click-to-navigate.
          Renders above the block's main content (z-10) and stops
          mouseDown from triggering drag. */}
      {isLinkBlock && <SheetLinkBadge block={block} />}

      <div className="p-2 h-full flex flex-col">
        <div
          className={cn(
            'flex items-center gap-1.5',
            // When the SheetLinkBadge is present at top-left, indent
            // the block header so the title doesn't overlap the icon.
            isLinkBlock && 'pl-6',
          )}
        >
          {onUpdateIcon ? (
            <IconPicker
              currentIcon={block.config.icon}
              blockType={block.type}
              blockColor={block.color}
              onSelectIcon={handleIconSelect}
            />
          ) : (
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${block.color}20` }}
            >
              <FallbackIcon className="w-3.5 h-3.5" />
            </div>
          )}
          <h3
            className="font-semibold text-[11px] leading-tight text-foreground whitespace-pre-line break-words flex-1 min-w-0"
            title={block.config.name}
          >
            {hyphenateName(
              block.config.name ?? '',
              // Width minus icon (24px) + gap (6px) + horizontal
              // padding (16px) leaves ≈ 104 px → ~15 chars at text-[11px].
              maxCharsForWidth(BLOCK_WIDTH_PX - 24 - 6 - 16),
            )}
          </h3>
        </div>
        <p className="text-[10px] leading-tight text-muted-foreground mt-1 break-words overflow-hidden line-clamp-2">
          {block.config.description || 'Konfigurera...'}
        </p>
        {onConvertToLayer && !viewOnly && (
          <button
            className="absolute top-0.5 right-0.5 w-4 h-4 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors z-10"
            title="Konvertera till lager"
            aria-label="Convert block to layer"
            data-testid="block-convert-to-layer"
            onClick={handleConvertToLayer}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Layers className="w-2.5 h-2.5" aria-hidden="true" />
          </button>
        )}
        {indicator && (indicator.hasDescription || indicator.attachmentCount > 0) && (
          <div
            className="absolute bottom-1 right-1.5 flex items-center gap-1 text-muted-foreground/70 pointer-events-none"
            aria-label={`${indicator.hasDescription ? 'Has description' : ''} ${indicator.attachmentCount > 0 ? `${indicator.attachmentCount} attachments` : ''}`.trim()}
          >
            {indicator.hasDescription && <FileText className="w-3 h-3" aria-hidden="true" />}
            {indicator.attachmentCount > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] font-mono">
                <Paperclip className="w-3 h-3" aria-hidden="true" />
                {indicator.attachmentCount}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
